import React, { useEffect, useMemo, useState } from 'react';
import { ChevronLeft, ChevronRight } from 'lucide-react';
import { apiGet } from '../../lib/api';

const TYPE_META = {
  received: { label: 'Received', dot: 'bg-slate-400' },
  submission: { label: 'Submission', dot: 'bg-rose-500' },
  technical: { label: 'Technical', dot: 'bg-sky-500' },
  commercial: { label: 'Commercial', dot: 'bg-violet-500' },
};
const URGENCY_RING = { overdue: 'ring-rose-400', today: 'ring-amber-400', tomorrow: 'ring-amber-300' };
const MONTHS = ['January', 'February', 'March', 'April', 'May', 'June', 'July', 'August', 'September', 'October', 'November', 'December'];
const pad = (n) => String(n).padStart(2, '0');

export default function TenderCalendar({ onPick }) {
  const [cursor, setCursor] = useState(() => { const d = new Date(); return { y: d.getFullYear(), m: d.getMonth() }; });
  const [events, setEvents] = useState([]);

  const range = useMemo(() => {
    const from = `${cursor.y}-${pad(cursor.m + 1)}-01`;
    const last = new Date(cursor.y, cursor.m + 1, 0).getDate();
    const to = `${cursor.y}-${pad(cursor.m + 1)}-${pad(last)}`;
    return { from, to, last };
  }, [cursor]);

  useEffect(() => {
    apiGet(`/enquiries/calendar?from=${range.from}&to=${range.to}`).then((r) => setEvents(r.events || [])).catch(() => setEvents([]));
  }, [range.from, range.to]);

  const byDay = useMemo(() => {
    const map = {};
    events.forEach((e) => { const d = (e.date || '').slice(8, 10); (map[d] = map[d] || []).push(e); });
    return map;
  }, [events]);

  const firstWeekday = new Date(cursor.y, cursor.m, 1).getDay();
  const cells = [];
  for (let i = 0; i < firstWeekday; i++) cells.push(null);
  for (let d = 1; d <= range.last; d++) cells.push(d);

  const shift = (delta) => setCursor((c) => {
    let m = c.m + delta, y = c.y;
    if (m < 0) { m = 11; y -= 1; } if (m > 11) { m = 0; y += 1; }
    return { y, m };
  });

  const todayStr = new Date().toISOString().slice(0, 10);

  return (
    <div data-testid="enq-calendar">
      <div className="flex items-center justify-between mb-3">
        <div className="flex items-center gap-2">
          <button onClick={() => shift(-1)} className="p-1.5 rounded hover:bg-slate-100" data-testid="cal-prev"><ChevronLeft size={16} /></button>
          <span className="font-display font-bold text-sm w-40 text-center">{MONTHS[cursor.m]} {cursor.y}</span>
          <button onClick={() => shift(1)} className="p-1.5 rounded hover:bg-slate-100" data-testid="cal-next"><ChevronRight size={16} /></button>
        </div>
        <div className="flex items-center gap-3 flex-wrap">
          {Object.entries(TYPE_META).map(([k, v]) => (
            <span key={k} className="flex items-center gap-1 text-[10px] text-slate-600"><span className={`w-2.5 h-2.5 rounded-full ${v.dot}`} /> {v.label}</span>
          ))}
        </div>
      </div>

      <div className="grid grid-cols-7 gap-1">
        {['Sun', 'Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat'].map((d) => (
          <div key={d} className="text-[10px] font-display font-bold uppercase tracking-wider text-slate-400 text-center py-1">{d}</div>
        ))}
        {cells.map((d, i) => {
          if (!d) return <div key={`e${i}`} className="min-h-[78px]" />;
          const ds = `${cursor.y}-${pad(cursor.m + 1)}-${pad(d)}`;
          const evs = byDay[pad(d)] || [];
          const isToday = ds === todayStr;
          return (
            <div key={ds} className={`min-h-[78px] border rounded-md p-1 ${isToday ? 'border-[#3b4872] bg-slate-50' : 'border-slate-200'}`} data-testid={`cal-day-${ds}`}>
              <div className={`text-[10px] font-bold ${isToday ? 'text-[#3b4872]' : 'text-slate-400'}`}>{d}</div>
              <div className="space-y-0.5 mt-0.5">
                {evs.slice(0, 3).map((e, j) => (
                  <button
                    key={j}
                    onClick={() => onPick && onPick(e.enquiry_id)}
                    title={`${e.title} · ${TYPE_META[e.deadline_type]?.label}`}
                    className={`w-full text-left text-[9px] leading-tight px-1 py-0.5 rounded bg-white border truncate hover:shadow-sm ${URGENCY_RING[e.urgency] ? `ring-1 ${URGENCY_RING[e.urgency]}` : 'border-slate-200'}`}
                    data-testid={`cal-event-${e.enquiry_id}-${e.deadline_type}`}
                  >
                    <span className={`inline-block w-1.5 h-1.5 rounded-full mr-1 align-middle ${TYPE_META[e.deadline_type]?.dot}`} />
                    {e.title}
                  </button>
                ))}
                {evs.length > 3 && <div className="text-[9px] text-slate-400 pl-1">+{evs.length - 3} more</div>}
              </div>
            </div>
          );
        })}
      </div>
    </div>
  );
}
